import React, { useMemo, useState } from 'react';
import { BedDouble, Send } from 'lucide-react';
import { computeClaimId } from '@/lib/claimRecords';
import { makeStay, stamp } from '@/lib/hotelStay';
import { HotelWindowCheck } from '@/components/spec/demo/HotelWindowCheck';

const PRINCIPAL = '0x3f51beeA4C65FBEEac6246d5Cef656b77338BBD8';

function Row({ k, v, id }) {
  return (
    <div className="flex flex-wrap justify-between gap-x-4 py-1.5 border-b border-white/5 text-sm">
      <span className="text-gray-500">{k}</span><span className="text-gray-200 text-right" data-testid={id}>{v}</span>
    </div>
  );
}

export function DemoHotelCancel({ demo }) {
  const [t0] = useState(() => Math.floor(Date.now() / 1000));
  const [late, setLate] = useState(false);
  const stay = useMemo(() => makeStay(late, t0), [late, t0]);
  const claim = useMemo(() => ({
    principal: PRINCIPAL, agentId: 'agent.example/travel-assistant', orderRef: stay.orderRef, claimType: 'cancellation',
    claimNumber: late ? 2 : 1, notice: late ? 'Cancel stay (late)' : 'Cancel stay', merchant: stay.merchant, expiry: t0 + 7 * 86400,
  }), [late, stay, t0]);
  const cid = useMemo(() => computeClaimId(claim), [claim]);
  const sent = demo.current?.claimId === cid ? demo.current : null;
  return (
    <section className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.03] p-6 mb-6" data-testid="demo-hotel-cancel">
      <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2"><BedDouble className="w-4 h-4 text-emerald-300" /> Hotel case. A guest's assistant cancels a stay</h2>
      <p className="text-sm text-gray-400 mb-4 max-w-3xl">The assistant booked direct, so it cancels direct. The hotel's signed receipt shows exactly when the request arrived, so nobody has to argue later about a no-show charge.</p>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="hotel-reservation">
          <Row k="Hotel" v={stay.merchant} id="hotel-merchant" />
          <Row k="Booking" v={stay.orderRef} id="hotel-booking" />
          <Row k="Room" v={stay.room} id="hotel-room" />
          <Row k="Check-in" v={stamp(stay.checkIn)} id="hotel-check-in" />
          <Row k="Free cancellation until" v={stamp(stay.deadline)} id="hotel-deadline" />
          <p className="text-xs text-gray-400 mt-3" data-testid="hotel-policy">Hotel policy: {stay.policy}</p>
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer mt-4">
            <input type="checkbox" className="accent-amber-300" checked={late} onChange={(e) => setLate(e.target.checked)} data-testid="hotel-late-toggle" />
            Late cancellation (check-in tomorrow)
          </label>
          <button type="button" onClick={() => demo.submit(claim)} className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-300 text-black px-4 py-2 text-sm font-semibold hover:bg-emerald-200 transition-colors" data-testid="hotel-cancel-btn">
            <Send className="w-4 h-4" /> Guest's assistant: cancel this stay
          </button>
        </div>
        <HotelWindowCheck sent={sent} stay={stay} />
      </div>
    </section>
  );
}
